import api from './index'

export interface WalletTransaction {
  id: number
  wallet_id: number
  type: 'deposit' | 'payment' | 'refund' | 'transfer'
  amount: number
  balance_after: number
  description: string
  reference_type: string
  reference_id: number | null
  created_by: number | null
  created_at: string
  owner_type: string
  owner_id: number
  wallet_balance: number
  owner_name: string
  owner_image: string
  owner_email: string | null
  admin_name: string | null
  status: 'completed' | 'pending' | 'cancelled' | 'failed' | 'refunded'
  cancelled_at?: string | null
  cancelled_by?: number | null
  cancel_reason?: string | null
}

export interface Payment {
  id: number
  match_id: number
  amount: number
  paid_amount: number
  bonus_discount: number
  computed_status: 'completed' | 'pending' | 'cancelled' | 'refunded'
  status?: string
  is_home: boolean
  payment_type: string
  created_at: string
  match_status: string
  products_fee: number
  products_paid: boolean
  referee_fee: number
  referee_fee_status: string | null
  club_home_id: number | null
  club_guest_id: number | null
  selected_stadium_id: number | null
  stadium_name: string | null
  home_club_name: string | null
  guest_club_name: string | null
  match_date: string | null
  /** Конец матча — точка отсчёта суток на возражение стадиона. */
  match_end_date?: string | null
  schedule_timezone?: string | null
  cash_dispute_deadline_at?: string | null
  /** finance-v2: состояние сбора обязательства. `manual_review` = ждёт человека. */
  collection_state?: string | null
  finance_model_version?: number | null
  obligation_version?: number | null
  /** Непокрытый остаток активной версии обязательства. */
  outstanding_amount?: number
  /**
   * Хотя бы один наличный взнос засчитан автоматически по истечении суток
   * молчания УВЕДОМЛЁННОГО стадиона, а не принят сотрудником в кассе.
   */
  deemed_cash_by_silence?: boolean
  /**
   * Тот же автозачёт, но стадион уведомить не удалось: получателей пуша нет.
   * Деньги закрыты вслепую — это накопленный риск, а не доказательство.
   */
  deemed_cash_without_notice?: boolean
  /** Почему уведомления не было (match_cash_notice, kind='undelivered'). */
  cash_notice_reason?: string | null
  cash_notice_at?: string | null
  splits: PaymentSplit[]
}

export interface PaymentSplit {
  id: number
  payment_id: number
  user_id: number | null
  amount: number
  status: string
  method: string
  bonus_discount: number
  user_name: string | null
  /**
   * Провенанс взноса. `deemed_cash_by_silence` — деньги засчитаны по молчанию
   * уведомлённого стадиона, `deemed_cash_without_notice` — уведомить было
   * некому. Ни то ни другое не кассовое подтверждение, и в споре они весят
   * по-разному.
   */
  receipt_source?: string | null
}

export interface AuditLogEntry {
  id: number
  admin_user_id: number
  action: string
  entity_type: string
  entity_id: number | null
  amount: number | null
  details: Record<string, any> | null
  before_state: Record<string, any> | null
  after_state: Record<string, any> | null
  reason: string | null
  ip_address: string | null
  created_at: string
  admin_name: string
  admin_image: string | null
}

export interface TransactionStats {
  total_deposits: number
  total_payments: number
  total_refunds: number
  total_transfers: number
  wallets_involved: number
}

export interface ConfirmPaymentResponse {
  success: boolean
  payment: Payment
  split_id?: number | null
  idempotent?: boolean
  products_only?: boolean
  products_amount?: number
  total_charged?: number
  paid_amount?: number
  total_amount?: number
  remaining_amount?: number
  is_fully_paid?: boolean
  products_settlement_pending?: boolean
  products_settlement_manual_required?: boolean
  referee_dispatch?: unknown
}

const financeApi = {
  // Transactions
  async getTransactions(params: {
    page?: number
    limit?: number
    type?: string
    reference_type?: string
    owner_type?: string
    search?: string
    from?: string
    to?: string
    min_amount?: number
    max_amount?: number
    sort_by?: string
    sort_order?: string
  }): Promise<{ transactions: WalletTransaction[]; total: number; stats: TransactionStats }> {
    const response = await api.get('/admin/finance/transactions', { params })
    return response.data
  },

  // Deposit with audit
  async deposit(data: {
    owner_type: string
    owner_id: number
    amount: number
    description?: string
    reference_type?: string
    reason?: string
  }, idempotencyKey: string): Promise<{ success: boolean; wallet: any; transaction: any }> {
    const response = await api.post('/admin/finance/deposit', data, {
      headers: { 'Idempotency-Key': idempotencyKey }
    })
    return response.data
  },

  // Withdraw with audit
  async withdraw(data: {
    owner_type: string
    owner_id: number
    amount: number
    reason: string
  }, idempotencyKey: string): Promise<{ success: boolean; wallet: any; transaction: any }> {
    const response = await api.post('/admin/finance/withdraw', data, {
      headers: { 'Idempotency-Key': idempotencyKey }
    })
    return response.data
  },

  // Payments
  async getPayments(params: {
    page?: number
    limit?: number
    status?: string
    /** finance-v2 состояние сбора: 'manual_review' достаёт обязательства на разборе. */
    collection_state?: string
    /** Провенанс автозачёта: 'deemed_cash_without_notice' достаёт накопленный риск. */
    provenance?: 'deemed_cash_by_silence' | 'deemed_cash_without_notice'
    match_id?: number
    stadium_id?: number
    from?: string
    to?: string
    search?: string
  }): Promise<{ payments: Payment[]; total: number }> {
    const response = await api.get('/admin/finance/payments', { params })
    return response.data
  },

  // Payment actions
  async refundPayment(paymentId: number, data: {
    amount?: number
    reason: string
    refund_to_wallet?: boolean
  }): Promise<{ success: boolean; refunded_amount: number; payment: Payment }> {
    const response = await api.post(`/admin/finance/payment/${paymentId}/refund`, data)
    return response.data
  },

  async cancelPayment(paymentId: number, data: {
    reason: string
    refund_to_wallet?: boolean
  }): Promise<{ success: boolean; refunded_amount: number; payment: Payment }> {
    const response = await api.post(`/admin/finance/payment/${paymentId}/cancel`, data)
    return response.data
  },

  async confirmPayment(paymentId: number, data: {
    reason?: string
  }): Promise<ConfirmPaymentResponse> {
    const response = await api.post(`/admin/finance/payment/${paymentId}/confirm`, data)
    return response.data
  },

  // Cancel wallet transaction
  async cancelTransaction(transactionId: number, reason: string): Promise<{
    success: boolean
    message: string
    transaction: any
    wallet: any
    paired_transaction: any | null
  }> {
    const response = await api.post('/admin/wallets/cancel-transaction', {
      transaction_id: transactionId,
      reason
    })
    return response.data
  },

  // Audit log
  async getAuditLog(params: {
    page?: number
    limit?: number
    action?: string
    entity_type?: string
    admin_user_id?: number
    from?: string
    to?: string
    search?: string
  }): Promise<{ entries: AuditLogEntry[]; total: number }> {
    const response = await api.get('/admin/finance/audit-log', { params })
    return response.data
  },

  // Единый детектор финансовых аномалий и утечек (контроль неправомерного использования средств)
  async getAnomalies(params?: {
    large_deposit_min?: number
    stuck_days?: number
    limit?: number
  }): Promise<AnomaliesResponse> {
    const response = await api.get('/admin/finance/anomalies', { params })
    return response.data
  },

  // Единый реестр всех денежных движений (wallet_transaction) с фильтрами
  async getLedger(params?: LedgerParams): Promise<LedgerResponse> {
    const response = await api.get('/admin/finance/ledger', { params })
    return response.data
  },

  // Выгрузка реестра в CSV (с теми же фильтрами). Возвращает Blob для скачивания.
  async exportLedgerCsv(params?: LedgerParams): Promise<Blob> {
    const response = await api.get('/admin/finance/ledger', {
      params: { ...params, format: 'csv' },
      responseType: 'blob'
    })
    return response.data as Blob
  },

  // Дешёвые счётчики для бейджей навигации
  async getBadges(): Promise<{
    pending_payouts: number
    negative_balances: number
    referee_escrow_anomalies: number
    referee_resolution_manual_reviews: number
    referee_no_show_disputes: number
    pending_closed_clubs: number
    financial_queues: number
  }> {
    const response = await api.get('/admin/finance/badges')
    return response.data
  },

  // Сводка движения денег: эмиссия (новые деньги в сети), custody-обязательства iGo, заработок
  async getCustody(): Promise<CustodySummary> {
    const response = await api.get('/admin/finance/custody')
    return response.data
  }
}

export interface CustodySummary {
  money_in_system: number
  emission: {
    topups_total: number
    bonuses_emitted_total: number
    // Поздние онлайн-оплаты матча сверх закрытого остатка, зачисленные на кошельки
    // (reference_type='psp_overflow_credit'). Необязательное: старый сервер не отдаёт.
    psp_overflow_credits_total?: number
  }
  funded_bonuses: {
    field_discount: number
    referee_subsidy: number
  }
  liabilities: {
    owed_to_users: number
    owed_to_clubs: number
    owed_to_stadiums: number
    owed_to_tournaments: number
    outstanding_withdrawals: number
    outstanding_withdrawals_count: number
  }
  receivables?: {
    stadium_provider_claims_total?: number
    stadium_provider_cash_refunds_total?: number
  }
  igo: {
    commission_balance: number
    referee_fees_in_pool: number         // собрано, ещё не выплачено судье (обязательство)
    referee_fees_collected: number       // алиас referee_fees_in_pool (обратная совместимость)
    referee_fees_disbursed: number       // выплачено судьям/стадионам (брутто)
    referee_commission_earned: number    // комиссия iGo с судейского фи
    withdrawals_paid_total: number
  }
  membership: {
    issued_total: number
    wallet_collected_total: number
    cash_recorded_total: number
    outstanding_total: number
    outstanding_count: number
    refunded_total: number
  }
  health: {
    negative_balance_wallets: number
    platform_balance_low: boolean
    platform_balance: number
    platform_bonus_budget_balance: number
    platform_balance_threshold: number
  }
}

export interface LedgerParams {
  page?: number
  limit?: number
  owner_type?: string
  reference_type?: string
  type?: string
  from?: string
  to?: string
  search?: string
}

export interface LedgerRow {
  id: number
  wallet_id: number
  type: string
  amount: number
  delta: number
  balance_after: number
  reference_type: string
  reference_id: number | null
  status: string
  created_at: string
  owner_type: string
  owner_id: number
  owner_name: string | null
  created_by: number | null
  created_by_name: string | null
  description?: string | null
}

export interface LedgerResponse {
  rows: LedgerRow[]
  total: number
  page: number
  limit: number
}

export interface AnomalyGroup {
  key: string
  title: string
  severity: 'critical' | 'high' | 'medium' | 'low'
  count: number
  total_amount: number
  items: Record<string, unknown>[]
  error: string | null
}

export interface AnomaliesResponse {
  generated_at: string
  params: { large_deposit_min: number; stuck_days: number; limit: number }
  total_count: number
  critical_count: number
  groups: AnomalyGroup[]
}

export default financeApi
